import { useState } from 'react';
import { OnboardingAssistanceType } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequestAssistance } from '@/hooks/queries';

/**
 * Stand-in for voter-file acquisition while pricing/self-serve is undecided: instead of letting a
 * manager upload data at signup, we capture a request and follow up. Records an
 * OnboardingAssistanceRequest (the request, not their voter data).
 */
export function VoterFileRequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { mutate: requestAssistance, isPending } = useRequestAssistance();

  // Start empty so the placeholder shows a grayed example the manager types their own email over.
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      // Reset for next time after it closes.
      setSubmitted(false);
      setNote('');
      setError(null);
    }
  };

  const submit = () => {
    setError(null);
    requestAssistance(
      {
        type: OnboardingAssistanceType.VoterFile,
        email: email.trim() || null,
        note: note.trim() || null,
      },
      {
        onSuccess: () => setSubmitted(true),
        onError: () => setError('Something went wrong sending your request. Please try again.'),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>Thanks — we&rsquo;re on it</DialogTitle>
              <DialogDescription>
                We&rsquo;ll reach out at {email || 'your email'} to help get your voter file set up.
                Nothing to upload right now.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>We&rsquo;ll get your voter file for you</DialogTitle>
              <DialogDescription>
                Don&rsquo;t have a voter file yet? Tell us how to reach you and our team will help
                you get one &mdash; no upload required.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="vf-email">Email</Label>
                <Input
                  id="vf-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@campaign.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vf-note">Anything we should know? (optional)</Label>
                <textarea
                  id="vf-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. which county or district you're running in, or what list you have access to"
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={isPending}>
                {isPending ? 'Sending…' : 'Request my voter file'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
